import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PrinterIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function VoucherPrint() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/reservations/${id}/voucher`).then((r) => setD(r.data)); }, [id]);
  if (!d) return <div className="min-h-screen flex items-center justify-center label-overline">Loading voucher…</div>;

  const { reservation: res, guest = {}, room = {}, property: prop = {}, platform = {} } = d;
  const L = platform.invoice_layout || {};
  const ref = (res.booking_ref || res.id.slice(0, 8)).toUpperCase();
  const download = async () => {
    try {
      const r = await api.get(`/reservations/${id}/voucher/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `voucher-${ref}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const rows = [
    ["Guest", guest.name],
    ["Phone / Email", [guest.phone, guest.email].filter(Boolean).join(" · ")],
    ["Check-in", `${fmt(res.check_in)} · from ${prop.check_in_time || "14:00"}`],
    ["Check-out", `${fmt(res.check_out)} · by ${prop.check_out_time || "11:00"}`],
    ["Nights", d.nights],
    ["Room", `${room.number || "—"} · ${room.room_type || ""}`],
    ["Guests", `${res.adults || 1} adult(s)${res.children ? `, ${res.children} child(ren)` : ""}`],
    ["Meal plan", res.plan_code || "EP"],
    ["Booking source", d.booking_source || "—"],
    ["Rate / night", inr(res.rate_per_night)],
    ["Room total (excl. taxes)", inr(res.total_amount)],
    ["Advance paid", `${inr(res.deposit_amount)}${res.deposit_amount > 0 && res.deposit_mode ? ` (${res.deposit_mode.toUpperCase()})` : ""}`],
  ];

  return (
    <div className="min-h-screen bg-muted/40 py-8 print:bg-white print:py-0">
      <div className="max-w-3xl mx-auto flex justify-end gap-2 mb-4 print:hidden">
        <Button variant="outline" onClick={download} data-testid="voucher-download-btn"><DownloadSimpleIcon size={16} className="mr-1" />Download PDF</Button>
        <Button onClick={() => window.print()} data-testid="voucher-print-btn"><PrinterIcon size={16} className="mr-1" />Print</Button>
      </div>
      <div className="max-w-3xl mx-auto bg-white text-black shadow-sm print:shadow-none" data-testid="voucher-sheet">
        <div className="px-8 py-6" style={{ background: L.header_bg_color || "#2F5D50", borderBottom: `2px solid ${L.header_accent_color || "#C8A24B"}` }}>
          {(L.header_group_overline || platform.group_name) && <p className="text-xs" style={{ color: L.header_accent_color || "#C8A24B" }}>◆ {L.header_group_overline || platform.group_name}</p>}
          <h1 className="font-display text-3xl mt-2 leading-tight" style={{ color: L.header_accent_color || "#C8A24B" }}>{L.header_property_display || prop.name}</h1>
          <p className="text-[10px] uppercase tracking-widest text-white mt-2">{L.header_location_badge || [prop.city, prop.state].filter(Boolean).join(", ")}</p>
        </div>
        <div className="px-8 py-6">
          <div className="flex justify-between border-b pb-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-neutral-500">Booking confirmation</p>
              <p className="text-xl font-bold" data-testid="voucher-ref">{ref}</p>
              <p className="text-xs text-neutral-500">{[prop.address, prop.phone, prop.email].filter(Boolean).join(" · ")}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-neutral-500">Status</p>
              <p className="font-semibold" data-testid="voucher-status">{(res.status || "").replace("_", " ").toUpperCase()}</p>
            </div>
          </div>
          <table className="w-full text-sm mt-4">
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k} className="border-b border-neutral-200">
                  <td className="py-2 w-56 font-semibold text-neutral-600 text-xs">{k}</td>
                  <td className="py-2" data-testid={`voucher-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {res.notes && <p className="text-xs mt-4"><b>Notes:</b> {res.notes}</p>}
          <p className="text-xs text-neutral-500 mt-6">Please carry a valid government photo ID for every guest at check-in. Taxes as applicable are charged on the final invoice.</p>
          <p className="text-xs text-neutral-500 mt-1">This is a computer-generated booking confirmation.</p>
        </div>
      </div>
    </div>
  );
}
